import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Context } from "../../../context/Context";
import { fetchActionsAPI } from "../../../utils/api/actions";
import { actionToCampaignItem } from "../operationUtils";
import { getCampaignText } from "./campaignActionsText";

// Campaign actions of one store (every app) in one status, as picker items.
// The list always comes from the API; `filter` only narrows what it returned
// (e.g. the "new requests" view of pending campaigns).
export function useStoreCampaigns({ storeId, status, filter, storeNames }) {
  const { lang, handleTokenExpiration } = useContext(Context);
  const text = getCampaignText(lang);
  const [state, setState] = useState({ actions: [], loading: false, error: "" });
  const [tick, setTick] = useState(0);
  const expiredRef = useRef(handleTokenExpiration);
  expiredRef.current = handleTokenExpiration;

  useEffect(() => {
    if (!storeId) {
      setState({ actions: [], loading: false, error: "" });
      return undefined;
    }
    const token = localStorage.getItem("auth-token");
    if (!token) {
      expiredRef.current();
      return undefined;
    }
    let active = true;
    setState((prev) => ({ ...prev, loading: true, error: "" }));
    fetchActionsAPI(token, { storeId, appName: "LUD", status, type: "campaigns" }).then((actions) => {
      if (!active) return;
      if (actions) setState({ actions, loading: false, error: "" });
      else setState({ actions: [], loading: false, error: text.loadError });
    });
    return () => {
      active = false;
    };
  }, [storeId, status, tick, text.loadError]);

  const items = useMemo(
    () =>
      state.actions
        .filter((action) => (filter ? filter(action) : true))
        .map((action) => actionToCampaignItem(action, storeNames)),
    [state.actions, filter, storeNames],
  );

  const reload = useCallback(() => setTick((value) => value + 1), []);
  return { items, loading: state.loading, error: state.error, reload };
}

// Explains a failed mutation. A 400 carries the server's own validation /
// business reason (plain text written for users, never internals), so it is
// shown as is; other statuses get a localized explanation.
export const describeError = (error, text) =>
  (error?.status === 400 && error.message) || text.errors[error?.status] || text.errors.generic;
